import React, { HTMLAttributes } from 'react';
import { useElementSize } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import { ImmutableEntity, ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { Node } from '@uncinc/react-drupal-core';

import {
  Button,
  Container,
  PDFReader,
  WysiwygField,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import ArticleMetadata from '../../../../components/ArticleMetadata';
import ThemesLabels from '../../../../components/ThemesLabels';
import BackButton from '../../../../components/BackButton';
import ShareWrapper from '../../../../components/ShareWrapper';
import { EntitiesHighlighted } from '../../../../components';

import {
  getOverviewUrlFromMenu,
} from '../../../../helpers';

import { NodeDocument as Type } from '../../../../types/nodes';

import {
  TAXONOMY_MEDIA_CATEGORY_MAGAZINE,
  TAXONOMY_MEDIA_CATEGORY_PUBLICATION,
  TAXONOMY_MEDIA_CATEGORY_CODE,
  SCREEN_TABLET,
} from '../../../../config/constants';

import './style.scss';

const buttons = {
  firstPage: (
    <i className="icon icon-first_page icon-variant-2" />
  ),
  previousPage: (
    <i className="icon icon-arrow-left icon-variant-2" />
  ),
  nextPage: (
    <i className="icon icon-arrow-right icon-variant-2" />
  ),
  lastPage: (
    <i className="icon icon-last_page icon-variant-2" />
  ),
  zoomIn: (
    <i className="icon icon-zoom_in icon-variant-2" />
  ),
  zoomOut: (
    <i className="icon icon-zoom_out icon-variant-2" />
  ),
  download: (
    <i className="icon icon-download icon-variant-2" />
  ),
  print: (
    <i className="icon icon-print icon-variant-2" />
  ),
};

interface DocumentProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const NodeDocumentPage: React.FC<DocumentProps> = ({ entity }) => {
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth < SCREEN_TABLET;
  const documentNode = entity.toJS();
  const title = documentNode.title[0].value;

  const body = documentNode.body[0]?.processed;
  const publicationDate = documentNode.field_publish_date[0].value;
  const themes = documentNode.field_themes;
  const pdf = documentNode.field_media_item[0].source;
  const documentType = documentNode.field_media_category?.[0]?.entity.name[0].value;
  const mediaCategoryId = documentNode.field_media_category?.[0]?.target_id;
  const documentArticles = documentNode.field_document_article;

  const footerMenu = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'footer']));
  const menuSecondary = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'secondary-navigation']));
  const menuMain = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'main']));
  const articlesOverviewUrl = getOverviewUrlFromMenu(menuMain, 'articles');

  let overviewUrl = '';

  if (mediaCategoryId === TAXONOMY_MEDIA_CATEGORY_PUBLICATION) {
    overviewUrl = getOverviewUrlFromMenu(footerMenu, 'publications');
  }

  if (mediaCategoryId === TAXONOMY_MEDIA_CATEGORY_CODE) {
    overviewUrl = getOverviewUrlFromMenu(footerMenu, 'codes');
  }

  if (mediaCategoryId === TAXONOMY_MEDIA_CATEGORY_MAGAZINE) {
    overviewUrl = getOverviewUrlFromMenu(menuSecondary, 'magazines');
  }

  const [containerRef, { width }] = useElementSize();
  const { t } = useTranslation();

  return (
    <div className={`NodeDocumentPage ${!mediaCategoryId && 'page-spacer'}`}>
      {mediaCategoryId && (
        <Container>
          <BackButton
            text={t(`content_types.document.${mediaCategoryId}_overview`)}
            to={overviewUrl}
          />
        </Container>
      )}

      <Container small>
        <div ref={containerRef}>
          <ArticleMetadata>
            <span>
              {documentType ? (
                <>{documentType}</>
              ) : (
                <>{t('content_types.document.type')}</>
              )}
            </span>
            <span className="dot">&bull;</span>
            <ArticleMetadata.PublicationDate publicationDate={new Date(publicationDate)} />
          </ArticleMetadata>

          <h1 itemProp="name" className="Document__hero__title">{title}</h1>

          {themes.length > 0 && (
            <ThemesLabels themes={themes} />
          )}

          {body && (
            <div className="Document__hero__body">
              <WysiwygField
                property="schema:text"
                html={body}
              />
            </div>
          )}
        </div>
      </Container>
      <div className="Document__wrapper">
        <Container>
          <div className="Document__document">
            <PDFReader
              // When deeloping locally, there's a fair chance you'll run into CORS errors
              // You can then use this url, for example:
              // url="https://cdn.filestackcontent.com/wcrjf9qPTCKXV3hMXDwK"
              url={pdf}
              width={width - 40}
              goToFirstPageButton={buttons.firstPage}
              goToPreviousPageButton={buttons.previousPage}
              goToNextPageButton={buttons.nextPage}
              goToLastPageButton={buttons.lastPage}
              zoomInButton={buttons.zoomIn}
              zoomOutButton={buttons.zoomOut}
              downloadButton={buttons.download}
              printButton={buttons.print}
              defaultScale={isMobile ? Number(1.2) : Number(1.5)}
            />
          </div>
        </Container>
      </div>

      <div className="Document__buttons">
        <Container small>
          <Button
            className="Button--secondary icon-after icon-download icon-variant-2"
            href={pdf}
            target="_blank"
            download="FileName"
            rel="noopener noreferrer"
          >
            {t('content_types.document.download')}
          </Button>
        </Container>
      </div>

      { /* Social Share Block */}
      <ShareWrapper withThemes themes={themes} />

      {documentArticles.length > 0 && (
        <EntitiesHighlighted
          title={t('content_types.document.related_articles.title')}
          link={articlesOverviewUrl}
          linkTitle={t('content_types.document.related_articles.link')}
        >
          <div className="row">
            {documentArticles.map((article) => (
              <div
                className="col-12 col-lg-4"
                key={article.target_id}
              >
                <Node
                  entity={Immutable.fromJS(article.entity)}
                  viewMode="teaser"
                />
              </div>
            ))}
          </div>
        </EntitiesHighlighted>
      )}

    </div>

  );
};
export default NodeDocumentPage;
